if (!customElements.get('ge-product-controls')) {
  customElements.define('ge-product-controls', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.config = JSON.parse(this.querySelector('script').textContent);
        this.root = this.closest('.thb-product-detail');
        if (!this.root) return;
        this.controller = new AbortController();
        this.signal = this.controller.signal;
        this.sectionId = this.dataset.section;
        this.form = document.getElementById(`product-form-${this.sectionId}`);
        this.setup();
        this.updateGalleryClearance = () => {
          if (!document.body.classList.contains('template-product-skincare')) return;
          const header = document.querySelector('.header-section');
          if (!header) return;
          const announcement = document.querySelector('.announcement-bar-section, .announcement-bar');
          const headerHeight = Math.max(header.offsetHeight, ...[...header.children].map((element) => element.getBoundingClientRect().height));
          const total = headerHeight + (announcement && !header.contains(announcement) ? announcement.offsetHeight : 0);
          this.root.style.setProperty('--ge-gallery-top', `${total}px`);
          const current = parseFloat(getComputedStyle(this.root).paddingTop) || 0;
          const gallery = this.root.querySelector('.product-image-container') || this.root;
          const naturalTop = gallery.getBoundingClientRect().top + window.scrollY - current;
          this.root.style.setProperty('--ge-product-clearance', `${Math.max(16, total + 16 - naturalTop)}px`);
        };
        requestAnimationFrame(this.updateGalleryClearance);
        window.addEventListener('resize', this.updateGalleryClearance, { signal: this.signal });
        window.addEventListener('load', this.updateGalleryClearance, { once: true, signal: this.signal });
        this.root.addEventListener('click', (event) => {
          const summary = event.target.closest('.ge-drawer-row summary, .ge-purchase [data-ge-drawer]');
          if (!summary) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.openDrawer(summary);
        }, { capture: true, signal: this.signal });
        this.root.addEventListener('click', (event) => this.handlePurchaseClick(event), { signal: this.signal });
        document.addEventListener('pointerdown', (event) => {
          if (!event.target.closest('.ge-purchase__wrap')) this.closePurchasePicker();
        }, { signal: this.signal });
        this.root.addEventListener('focusout', (event) => {
          if (event.target.closest('.ge-purchase__wrap') && !event.relatedTarget?.closest('.ge-purchase__wrap')) this.closePurchasePicker();
        }, { signal: this.signal });
        this.root.addEventListener('keydown', (event) => {
          const picker = this.root.querySelector('.ge-purchase__picker');
          if (!picker || picker.hidden) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closePurchasePicker(true);
          } else if (event.target.closest('.ge-purchase__picker') && ['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const buttons = [...picker.querySelectorAll('button:not(:disabled)')];
            const index = buttons.indexOf(document.activeElement);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
            buttons[next]?.focus();
          }
        }, { signal: this.signal });
        this.root.addEventListener('change', (event) => {
          if (event.target.matches('[name="id"]')) this.updateQuantity();
          this.syncPurchaseDetails();
        }, { signal: this.signal });
        this.observer = new MutationObserver(() => this.setup());
        this.observer.observe(this.root, {
          childList: true, subtree: true, attributes: true, attributeFilter: ['data-plan-mode']
        });
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearInterval(this.policyTimer);
      this.railObserver?.disconnect();
      this.stickyObserver?.disconnect();
      if (this.preorderAnchor && this.preorderHome) this.preorderHome.append(this.preorderAnchor);
      this.stickyDock?.remove();
      this.stickyPlaceholder?.remove();
      if (this.drawer) {
        if (typeof OverlayManager !== 'undefined') OverlayManager.close(this.drawer);
        this.restoreContent();
        this.drawer.remove();
      }
      this.controller = null;
    }

    setup() {
      if (!this.isConnected) return;
      this.setupSizeDropdowns();
      this.setupPreorderCopy();
      this.setupPurchasePill();
      this.syncPurchaseDetails();
      this.setupQuantity();
      this.setupStickyPreorder();
      this.setupDrawers();
      this.setupPolicies();
      this.setupProductRail();
    }

    selectChevron() {
      const arrow = document.createElement('span');
      arrow.className = 'ge-select-chevron';
      arrow.innerHTML = '<svg width="14" height="9" viewBox="0 0 14 9" aria-hidden="true"><path d="m1 1 6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
      return arrow;
    }

    setupSizeDropdowns() {
      const sizeSwitch = this.root.querySelector('[data-ge-size-switch]');
      const options = sizeSwitch?.querySelector('.ge-skin-size__options');
      if (options && !options.querySelector('select')) {
        const links = [...options.querySelectorAll('[data-ge-size-choice]')];
        if (links.length) {
          const select = document.createElement('select');
          const heading = sizeSwitch.querySelector('.ge-skin-size__heading');
          if (heading) {
            heading.textContent = this.config.sizeLabel || 'Size';
            heading.id = `GeSizeLabel-${this.sectionId}`;
            select.setAttribute('aria-labelledby', heading.id);
          }
          for (const link of links) {
            const name = link.querySelector('.ge-skin-size__name')?.textContent.trim();
            const detail = link.querySelector('.ge-skin-size__detail')?.textContent.replace(/[·—]/g, '').replace(/\s+/g, ' ').trim();
            select.add(new Option([name, detail].filter(Boolean).join(' '), link.getAttribute('href'), false, link.getAttribute('aria-current') === 'true'));
          }
          options.classList.add('ge-size-select');
          options.replaceChildren(select, this.selectChevron());
          select.addEventListener('change', () => {
            const next = new URL(select.value, window.location.href);
            const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
            if (preview) next.searchParams.set('preview_theme_id', preview);
            window.location.assign(next.href);
          }, { signal: this.signal });
        }
      }
      if (options?.querySelector('select') && !options.querySelector('.ge-designed-size')) {
        const select = options.querySelector('select');
        const menu = document.createElement('details');
        menu.className = 'ge-designed-size';
        const summary = document.createElement('summary');
        summary.append(document.createTextNode(select.selectedOptions[0].textContent), this.selectChevron());
        const list = document.createElement('div');
        list.className = 'ge-designed-size-list';
        for (const option of select.options) {
          const link = document.createElement('a');
          link.textContent = option.textContent;
          const url = new URL(option.value, window.location.href);
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          link.href = url.href;
          if (option.selected) link.setAttribute('aria-current', 'page');
          list.append(link);
        }
        menu.append(summary, list);
        options.append(menu);
        select.hidden = true;
        options.classList.add('ge-custom-size');
        menu.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') { menu.open = false; summary.focus(); }
        }, { signal: this.signal });
        document.addEventListener('pointerdown', (event) => {
          if (!menu.contains(event.target)) menu.open = false;
        }, { signal: this.signal });
      }
      this.root.querySelectorAll('variant-selects fieldset[data-handle]').forEach((fieldset) => {
        if (!document.body.classList.contains('template-product-skincare')) return;
        if (!/^(size|sizes|volume|amount)$/.test(fieldset.dataset.handle)) return;
        let select = fieldset.querySelector('select');
        if (!select) {
          const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
          if (!radios.length) return;
          select = document.createElement('select');
          select.name = `options[${radios[0].name}]`;
          select.setAttribute('form', radios[0].getAttribute('form'));
          for (const radio of radios) {
            const option = new Option(radio.value, radio.value, false, radio.checked);
            Object.assign(option.dataset, radio.dataset);
            select.add(option);
            const label = fieldset.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
            label?.remove();
            radio.remove();
          }
          const control = document.createElement('div');
          control.className = 'ge-size-select';
          control.append(select, this.selectChevron());
          fieldset.append(control);
          const picker = fieldset.closest('variant-selects');
          picker.updateOptions?.();
          picker.setDisabled?.();
          picker.prefetchProductUrls?.();
        }
        if (fieldset.classList.contains('ge-size-field')) return;
        fieldset.classList.add('ge-size-field');
        const label = fieldset.querySelector('.form__label');
        if (label) {
          label.id ||= `GeSizeLabel-${fieldset.closest('variant-selects').id || this.sectionId}-${fieldset.dataset.index}`;
          select.setAttribute('aria-labelledby', label.id);
          label.querySelector('.form__label__value')?.remove();
          [...label.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE).forEach((node) => { node.textContent = node.textContent.replace(/:\s*$/, ''); });
        }
        const control = select.parentElement;
        control.classList.add('ge-size-select');
        control.querySelector('.select-arrow')?.remove();
        if (!control.querySelector('.ge-select-chevron')) control.append(this.selectChevron());
      });
    }

    setupPreorderCopy() {
      const offer = this.root.querySelector('[data-ge-preorder-offer]');
      if (!offer || offer.dataset.cleaned === 'true') return;
      offer.dataset.cleaned = 'true';
      const copy = document.createElement('div');
      copy.className = 'ge-preorder-copy';
      offer.querySelectorAll('.ge-skin-choice__check').forEach((line) => {
        if (!/2027/i.test(line.textContent)) copy.append(line);
      });
      offer.querySelector('.ge-skin-purchase')?.remove();
      const size = this.root.querySelector('[data-ge-size-switch]');
      if (size) size.after(copy);
      else offer.before(copy);
      this.root.querySelector('[data-ge-policy-open="dispatch"]')?.closest('li')?.remove();
    }

    setupPurchasePill() {
      const offer = this.root.querySelector('[data-ge-offers]');
      const purchase = offer?.querySelector('.ge-skin-purchase');
      if (!purchase || purchase.classList.contains('ge-purchase-pill')) return;
      const choices = [...purchase.children].filter((choice) => choice.querySelector('input[type="radio"]'));
      if (choices.length < 2) return;
      const details = document.createElement('div');
      details.className = 'ge-purchase-details';
      let frequency;
      for (const choice of choices) {
        const radio = choice.querySelector('input[type="radio"]');
        const main = choice.querySelector('.ge-skin-choice__main');
        const head = choice.querySelector('.ge-skin-choice__head');
        if (!main || !head) continue;
        choice.classList.add('ge-purchase__option');
        choice.dataset.choice = radio.value;
        radio.tabIndex = -1;
        const panel = document.createElement('div');
        panel.className = 'ge-skin-choice';
        panel.dataset.purchaseDetail = radio.value;
        [...main.children].filter((node) => node !== head).forEach((node) => panel.append(node));
        if (radio.value === 'once') {
          panel.classList.add('ge-purchase__once-note');
          panel.textContent = this.config.onceDescription || 'One-time purchase. No subscription.';
        } else {
          frequency = panel.querySelector('.ge-skin-choice__freqs');
          frequency?.remove();
          panel.classList.add('ge-purchase__subscription-content');
          panel.replaceChildren();
          const benefits = document.createElement('ul');
          benefits.className = 'ge-purchase__benefits';
          const delivery = document.createElement('li');
          delivery.dataset.purchaseDelivery = '';
          delivery.className = 'ge-purchase__cadence';
          delivery.setAttribute('aria-live', 'polite');
          const flexibility = document.createElement('li');
          flexibility.textContent = this.config.subscriptionFlexibility || 'Pause, skip or cancel anytime';
          benefits.append(delivery, flexibility);
          const terms = document.createElement('div');
          terms.className = 'ge-purchase__terms';
          const offerText = document.createElement('p');
          const offerCopy = document.createElement('span');
          offerCopy.dataset.purchaseTerms = '';
          offerText.append(offerCopy, ' ', this.purchaseDrawerLink('subscription-offer', this.config.detailsLabel || 'Details'));
          const consent = document.createElement('p');
          consent.textContent = this.config.subscriptionConsent || 'By enrolling, you consent to the recurring charge for your subscription.';
          terms.append(offerText, consent, this.purchaseDrawerLink('auto-replenish', this.config.autoReplenishLabel || 'About Auto-Replenish'));
          panel.append(benefits, terms);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ge-purchase__choice';
        button.dataset.gePurchaseChoice = radio.value;
        button.append(main);
        if (radio.value === 'subscribe') {
          button.classList.add('ge-purchase__subscribe');
          button.setAttribute('aria-controls', `GeDeliveryPicker-${this.sectionId}`);
          button.setAttribute('aria-expanded', 'false');
          const chevron = document.createElement('span');
          chevron.className = 'ge-purchase__chevron';
          chevron.setAttribute('aria-hidden', 'true');
          button.append(chevron);
        }
        choice.append(button);
        details.append(panel);
      }
      const subscription = choices.find((choice) => choice.querySelector('input[value="subscribe"]'));
      if (subscription) purchase.prepend(subscription);
      purchase.classList.add('ge-purchase-pill', 'ge-purchase__track');
      purchase.setAttribute('role', 'group');
      purchase.setAttribute('aria-label', this.config.purchaseTypeLabel || 'Purchase type');
      const slider = document.createElement('div');
      slider.className = 'ge-purchase__slider';
      slider.setAttribute('aria-hidden', 'true');
      purchase.prepend(slider);
      const box = document.createElement('div');
      box.className = 'ge-purchase-box ge-purchase ge-purchase--pearl';
      const wrap = document.createElement('div');
      wrap.className = 'ge-purchase__wrap';
      const picker = document.createElement('div');
      picker.className = 'ge-purchase__picker';
      picker.id = `GeDeliveryPicker-${this.sectionId}`;
      picker.hidden = true;
      picker.setAttribute('role', 'group');
      picker.setAttribute('aria-label', this.config.deliveryFrequencyLabel || 'Delivery frequency');
      const title = document.createElement('p');
      title.className = 'ge-purchase__picker-title';
      title.textContent = this.config.deliveryFrequencyLabel || 'Delivery frequency';
      const options = document.createElement('div');
      options.dataset.gePlanChoices = '';
      picker.append(title, options);
      purchase.before(box);
      wrap.append(purchase, picker);
      if (frequency) { frequency.hidden = true; wrap.append(frequency); }
      box.append(wrap, details);
      for (const type of ['subscription-offer', 'auto-replenish']) {
        const source = document.createElement('div');
        source.hidden = true;
        source.dataset.geDrawerContent = type;
        const content = document.createElement('div');
        content.className = 'accordion__content rte';
        source.append(content);
        box.append(source);
      }
      this.syncPurchaseDetails();
    }

    purchaseDrawerLink(type, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ge-purchase__link';
      button.dataset.geDrawer = type;
      button.dataset.drawerTitle = type === 'subscription-offer' ? (this.config.subscriptionDetailsTitle || 'Subscription offer details') : label;
      button.textContent = label;
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-controls', `GeProductDetails-${this.sectionId}`);
      button.setAttribute('aria-expanded', 'false');
      return button;
    }

    closePurchasePicker(restoreFocus = false) {
      const picker = this.root.querySelector('.ge-purchase__picker');
      if (!picker || picker.hidden) return;
      picker.hidden = true;
      const button = this.root.querySelector('.ge-purchase__subscribe');
      button?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) button?.focus();
    }

    handlePurchaseClick(event) {
      const choice = event.target.closest('[data-ge-purchase-choice]');
      if (choice) {
        if (choice.dataset.gePurchaseChoice === 'once') { this.closePurchasePicker(); return; }
        const picker = this.root.querySelector('.ge-purchase__picker');
        const open = picker.hidden;
        picker.hidden = !open;
        choice.setAttribute('aria-expanded', String(open));
        if (open) picker.querySelector('[aria-pressed="true"]')?.focus();
      }
      const plan = event.target.closest('[data-ge-plan-choice]');
      if (plan) {
        const select = this.root.querySelector('[data-ge-freq]');
        select.value = plan.dataset.gePlanChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.closePurchasePicker(true);
      }
    }

    syncPurchaseDetails() {
      const offer = this.root.querySelector('[data-ge-offers]');
      if (!offer) return;
      const selected = offer.querySelector('input[name^="ge-plan-"]:checked');
      const purchase = offer.querySelector('.ge-purchase__track');
      if (!purchase) return;
      const frequency = offer.querySelector('[data-ge-freq]');
      const setText = (node, text) => { if (node && node.textContent !== text) node.textContent = text; };
      const isSubscription = selected?.value === 'subscribe';
      purchase.dataset.selected = selected?.value || 'once';
      if (!isSubscription) this.closePurchasePicker();
      const options = [...(frequency?.options || [])];
      const signature = JSON.stringify(options.map((option) => [option.value, option.textContent, option.disabled]));
      const pickerOptions = offer.querySelector('[data-ge-plan-choices]');
      if (pickerOptions.dataset.signature !== signature) {
        pickerOptions.dataset.signature = signature;
        pickerOptions.replaceChildren(...options.map((option) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ge-purchase__week';
          button.dataset.gePlanChoice = option.value;
          button.disabled = option.disabled;
          const label = document.createElement('span');
          label.textContent = option.textContent.trim().replace(/^deliver(?:y)?\s+every\s+/i, 'Every ');
          const check = document.createElement('span');
          check.className = 'ge-purchase__check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = '✓';
          button.append(label, check);
          return button;
        }));
      }
      pickerOptions.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.gePlanChoice === frequency?.value)));
      const price = Number(offer.dataset.priceCents);
      const subPrice = Number(offer.dataset.subCents);
      const savings = price > 0 && Number.isFinite(subPrice) ? Math.max(0, Math.round((price - subPrice) * 100 / price)) : 0;
      purchase.querySelectorAll('.ge-purchase__option').forEach((choice) => {
        const radio = choice.querySelector('input[type="radio"]');
        choice.classList.toggle('is-selected', radio.checked);
        choice.querySelector('button').setAttribute('aria-pressed', String(radio.checked));
        const label = radio.value === 'once' ? (this.config.buyOnceLabel || 'Buy Once') :
          (savings ? (this.config.subscribeSavingsLabel || 'Subscribe & Save __SAVINGS__%').replace(/\{\{ savings \}\}|__SAVINGS__/, savings) : (this.config.subscribeLabel || 'Subscribe'));
        const plainLabel = label.replace(/&amp;/g, '&');
        // Compact toggle row: short label, price on the right, savings as a small badge.
        let shortLabel = plainLabel;
        if (radio.value !== 'once') {
          shortLabel = this.config.subscribeShortLabel || 'Subscribe';
          const btn = choice.querySelector('button');
          let badge = btn && btn.querySelector('.ge-purchase__badge');
          if (btn && !badge) {
            badge = document.createElement('span');
            badge.className = 'ge-purchase__badge';
            badge.setAttribute('aria-hidden', 'true');
            btn.append(badge);
          }
          if (badge) {
            badge.hidden = !savings;
            setText(badge, (this.config.saveBadgeLabel || 'Save __SAVINGS__%').replace('__SAVINGS__', savings));
          }
        }
        setText(choice.querySelector('.ge-skin-choice__label'), shortLabel);
        if (radio.getAttribute('aria-label') !== plainLabel) radio.setAttribute('aria-label', plainLabel);
        const choiceBtn = choice.querySelector('button');
        if (choiceBtn && choiceBtn.getAttribute('aria-label') !== plainLabel) choiceBtn.setAttribute('aria-label', plainLabel + (radio.value !== 'once' ? ` (${shortLabel})` : ''));
      });
      const cadence = (frequency?.selectedOptions[0]?.textContent || '').trim().replace(/^deliver(?:y)?\s+/i, '').replace(/^every\s+/i, '');
      const deliveryCopy = (this.config.subscriptionDelivery || 'Delivery every __FREQUENCY__').replace('__FREQUENCY__', cadence);
      const deliveryItem = offer.querySelector('[data-purchase-delivery]');
      deliveryItem.hidden = !cadence;
      setText(deliveryItem, deliveryCopy);
      let terms = (this.config.subscriptionOffer || '*First-time subscribers get __SAVINGS__% off their first 3 orders, delivered within 12 months of the initial order placement.').replace('__SAVINGS__', savings);
      if (!savings) terms = this.config.subscriptionNoOffer || 'Your subscription follows the selected delivery plan.';
      setText(offer.querySelector('[data-purchase-terms]'), terms);
      const contents = {
        'subscription-offer': [terms, deliveryCopy, this.config.subscriptionFlexibility || 'Pause, skip or cancel anytime'],
        'auto-replenish': [this.config.subscriptionConsent || 'By enrolling, you consent to the recurring charge for your subscription.', deliveryCopy, this.config.subscriptionFlexibility || 'Pause, skip or cancel anytime']
      };
      for (const [type, paragraphs] of Object.entries(contents)) {
        const source = offer.querySelector(`[data-ge-drawer-content="${type}"]`);
        const content = this.summary?.dataset.geDrawer === type ? this.content : source.querySelector('.accordion__content');
        const text = paragraphs.filter(Boolean);
        const copySignature = JSON.stringify(text);
        if (content && content.dataset.copySignature !== copySignature) {
          content.dataset.copySignature = copySignature;
          content.replaceChildren(...text.map((text) => { const paragraph = document.createElement('p'); paragraph.textContent = text; return paragraph; }));
        }
      }
      offer.querySelectorAll('[data-purchase-detail]').forEach((panel) => { panel.hidden = panel.dataset.purchaseDetail !== selected?.value; });
    }

    setupQuantity() {
      if (!this.form?.isConnected) return;
      const add = this.form.querySelector('.single-add-to-cart-button');
      if (!add) return;
      let row = add.closest('.product-form__checkout-row');
      if (!row) {
        row = document.createElement('div');
        row.className = 'product-form__checkout-row';
        add.before(row);
        row.append(add);
      }
      row.classList.add('ge-quantity-row');
      if (!this.quantity?.isConnected) {
        this.quantity = document.createElement('div');
        this.quantity.className = 'ge-product-quantity';
        this.quantityInput = document.createElement('input');
        this.quantityInput.type = 'number';
        this.quantityInput.name = 'quantity';
        this.quantityInput.inputMode = 'numeric';
        this.quantityInput.required = true;
        this.quantityInput.setAttribute('aria-label', this.config.quantityLabel);
        this.decrease = document.createElement('button');
        this.decrease.type = 'button';
        this.decrease.setAttribute('aria-label', this.config.decreaseQuantity);
        this.decrease.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M1 8h14" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
        this.increase = document.createElement('button');
        this.increase.type = 'button';
        this.increase.setAttribute('aria-label', this.config.increaseQuantity);
        this.increase.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M1 8h14M8 1v14" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
        this.quantity.append(this.decrease, this.quantityInput, this.increase);
        row.prepend(this.quantity);
        this.updateQuantity();
        this.decrease.addEventListener('click', () => this.stepQuantity(-1), { signal: this.signal });
        this.increase.addEventListener('click', () => this.stepQuantity(1), { signal: this.signal });
        this.quantityInput.addEventListener('input', () => {
          if (this.quantityInput.checkValidity()) this.quantityValue = this.quantityInput.valueAsNumber;
          this.updateQuantityButtons();
        }, { signal: this.signal });
        this.quantityInput.addEventListener('change', () => this.updateQuantity(), { signal: this.signal });
      }
      [...this.form.elements].filter((input) => input.name === 'quantity' && !this.quantity.contains(input)).forEach((input) => {
        input.removeAttribute('name');
        input.disabled = true;
        const oldControl = input.closest(`[id="quantity-${this.sectionId}"]`) || input.closest('quantity-selector');
        if (oldControl) oldControl.hidden = true;
      });
    }

    stepQuantity(direction) {
      const step = Number(this.quantityInput.step) || 1;
      this.quantityValue = (this.quantityInput.valueAsNumber || Number(this.quantityInput.min)) + direction * step;
      this.updateQuantity();
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateQuantityButtons() {
      const value = this.quantityInput.valueAsNumber;
      const min = Number(this.quantityInput.min);
      const max = this.quantityInput.hasAttribute('max') ? Number(this.quantityInput.max) : Infinity;
      this.decrease.disabled = value <= min;
      this.increase.disabled = value >= max;
    }

    updateQuantity() {
      if (!this.quantityInput) return;
      const variantId = this.form.querySelector('[name="id"]')?.value;
      const rule = this.config.variants[variantId] || { min: 1, max: null, step: 1 };
      const min = rule.min || 1;
      const step = rule.step || 1;
      const max = rule.max ?? null;
      let value = Math.max(min, Number(this.quantityValue) || min);
      value = min + Math.ceil((value - min) / step) * step;
      if (max !== null && value > max) value = min + Math.floor((max - min) / step) * step;
      this.quantityValue = value;
      this.quantityInput.min = String(min);
      this.quantityInput.step = String(step);
      if (max === null) this.quantityInput.removeAttribute('max');
      else this.quantityInput.max = String(max);
      this.quantityInput.value = String(value);
      this.updateQuantityButtons();
    }

    setupStickyPreorder() {
      if (this.stickyObserver) return;
      const anchor = this.root.querySelector('#pp-cleanslate1-anchor');
      if (!anchor) return;
      this.preorderAnchor = anchor;
      this.preorderHome = anchor.parentElement;
      const placeholder = document.createElement('div');
      placeholder.className = 'ge-preorder-placeholder';
      placeholder.style.height = `${anchor.getBoundingClientRect().height || 52}px`;
      anchor.before(placeholder);
      placeholder.append(anchor);
      this.stickyPlaceholder = placeholder;
      const dock = document.createElement('div');
      dock.className = 'ge-preorder-dock';
      document.body.append(dock);
      this.stickyDock = dock;
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          placeholder.append(anchor);
          dock.classList.remove('is-visible');
        } else {
          dock.append(anchor);
          requestAnimationFrame(() => dock.classList.add('is-visible'));
        }
      }, { rootMargin: '-100px 0px 0px 0px', threshold: 0 });
      this.stickyObserver.observe(placeholder);
    }

    setupDrawers() {
      if (typeof OverlayManager === 'undefined') return;
      this.root.querySelectorAll('.product-information .accordion details').forEach((details) => {
        const row = details.closest('.accordion');
        if (row.classList.contains('ge-drawer-row')) return;
        const summary = details.querySelector('summary');
        if (!summary || !details.querySelector('.accordion__content')) return;
        row.classList.add('ge-drawer-row');
        details.open = false;
        summary.setAttribute('aria-haspopup', 'dialog');
        summary.setAttribute('aria-controls', `GeProductDetails-${this.sectionId}`);
        summary.setAttribute('aria-expanded', 'false');
      });
    }

    createDrawer() {
      this.drawer = document.createElement('div');
      this.drawer.className = 'side-panel ge-product-details-drawer thb-product-detail';
      this.drawer.id = `GeProductDetails-${this.sectionId}`;
      this.drawer.tabIndex = -1;
      this.drawer.inert = true;
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.drawer.setAttribute('aria-labelledby', `GeProductDetailsTitle-${this.sectionId}`);
      this.drawer.innerHTML = `<div class="side-panel-inner"><div class="side-panel-header"><div><h4 class="body-font" id="GeProductDetailsTitle-${this.sectionId}"></h4><button type="button" class="side-panel-close"><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="m2 2 12 12M14 2 2 14" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></button></div></div><div class="side-panel-content"><div class="accordion"></div></div></div>`;
      this.drawer.querySelector('button').setAttribute('aria-label', this.config.closeLabel);
      this.drawer.querySelector('button').addEventListener('click', () => OverlayManager.close(this.drawer), { signal: this.signal });
      document.body.append(this.drawer);
      document.addEventListener('panel:closed', (event) => {
        if (event.detail.panel === this.drawer) this.restoreContent();
      }, { signal: this.signal });
    }

    openDrawer(summary) {
      if (!this.drawer) this.createDrawer();
      this.restoreContent();
      const content = summary.dataset.geDrawer ? this.root.querySelector(`[data-ge-drawer-content="${summary.dataset.geDrawer}"] .accordion__content`) : summary.parentElement.querySelector('.accordion__content');
      if (!content) return;
      this.content = content;
      this.contentHome = content.parentElement;
      this.contentNext = content.nextSibling;
      this.summary = summary;
      const title = this.drawer.querySelector('h4');
      title.textContent = summary.dataset.drawerTitle || [...summary.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join('').trim() || summary.textContent.trim();
      this.drawer.querySelector('.side-panel-content .accordion').append(content);
      this.drawer.querySelector('.side-panel-content').scrollTop = 0;
      summary.setAttribute('aria-expanded', 'true');
      OverlayManager.open(this.drawer);
    }

    restoreContent() {
      if (!this.content) return;
      this.contentHome.insertBefore(this.content, this.contentNext);
      this.summary.setAttribute('aria-expanded', 'false');
      this.content = null;
    }

    setupProductRail() {
      const container = this.root.querySelector('.complementary-products--inner');
      if (!container || container.classList.contains('ge-product-rail')) return;
      const cards = [...container.querySelectorAll(':scope > .product-cart-item')];
      if (!cards.length) return;
      container.classList.add('ge-product-rail');
      const heading = container.querySelector('.complementary-products--heading');
      const header = document.createElement('div');
      header.className = 'ge-product-rail-header';
      const track = document.createElement('div');
      track.className = 'ge-product-rail-track';
      track.tabIndex = 0;
      track.setAttribute('aria-label', heading?.textContent.trim() || '');
      track.append(...cards);
      const progress = document.createElement('span');
      progress.className = 'ge-product-rail-progress';
      progress.setAttribute('aria-hidden', 'true');
      progress.append(document.createElement('span'));
      const previous = document.createElement('button');
      const next = document.createElement('button');
      [previous, next].forEach((button, index) => {
        button.type = 'button';
        button.setAttribute('aria-label', index ? this.config.nextLabel : this.config.previousLabel);
        button.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="${index ? 'M3 12h18m-6-6 6 6-6 6' : 'M21 12H3m6-6-6 6 6 6'}" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>`;
        button.addEventListener('click', () => track.scrollBy({ left: (index ? 1 : -1) * (cards[0].getBoundingClientRect().width + 16), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }), { signal: this.signal });
      });
      if (heading) header.append(heading);
      header.append(progress, previous, next);
      container.prepend(header);
      container.append(track);
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const fraction = max > 1 ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 1;
        progress.firstElementChild.style.width = `${max > 1 ? (1 + fraction * (cards.length - 1)) / cards.length * 100 : 100}%`;
        previous.disabled = track.scrollLeft < 1;
        next.disabled = max < 1 || track.scrollLeft >= max - 1;
      };
      track.addEventListener('scroll', update, { passive: true, signal: this.signal });
      this.railObserver = new ResizeObserver(update);
      this.railObserver.observe(track);
      update();
    }

    setupPolicies() {
      const list = this.root.querySelector('[data-ge-reassure]');
      if (!list || list.classList.contains('ge-policy-rotation') || list.children.length < 2) return;
      const wrapper = document.createElement('ge-policy-rotator');
      list.before(wrapper);
      wrapper.append(list);
      list.classList.add('ge-policy-rotation');
      const items = [...list.children];
      let index = 0;
      let paused = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const show = () => items.forEach((item, position) => {
        const active = position === index;
        item.classList.toggle('is-current', active);
        item.inert = !active;
        item.setAttribute('aria-hidden', String(!active));
      });
      const advance = () => { index = (index + 1) % items.length; show(); };
      if (paused) {
        items.forEach((item) => { item.classList.add('is-current'); item.inert = false; item.setAttribute('aria-hidden', 'false'); });
        list.classList.add('ge-policy-static');
        return;
      }
      show();
      this.policyTimer = setInterval(() => {
        if (paused || document.hidden || wrapper.matches(':hover, :focus-within') || document.body.classList.contains('open-cc')) return;
        advance();
      }, 5000);
    }
  });
}

/* GE: Essential Pre-Order owns and constantly re-renders its button spans ("Preorder •", "$33.6"),
   so rewriting its text just fights the app. Instead, read its values and expose formatted copies
   on the theme-owned <button> (CSS vars + aria-label); skincare-pdp.css paints them via ::after.
   We never write inside app nodes and only observe childList/characterData, so our own
   attribute/style writes cannot retrigger the observer (no loop / headless hang). */
(() => {
  if (!document.body || !document.body.classList.contains('template-product-skincare')) return;
  const fmt = (txt) => {
    if (!txt) return '';
    const m = txt.replace(/,/g, '').match(/(\D*?)(\d+(?:\.\d+)?)/);
    return m ? `${m[1].trim()}${parseFloat(m[2]).toFixed(2)}` : txt.trim();
  };
  const sync = (btn) => {
    const reg = btn.querySelector('[data-essential-preorder-element="button-regular"]');
    if (!reg) return;
    const cmp = btn.querySelector('[data-essential-preorder-element="button-compare-at"]');
    const price = fmt(reg.textContent);
    const compare = cmp && cmp.textContent.trim() ? fmt(cmp.textContent) : '';
    const key = `${price}|${compare}`;
    if (btn.dataset.gePreorder === key) return;
    btn.dataset.gePreorder = key;
    btn.style.setProperty('--ge-po-price', JSON.stringify(price));
    btn.style.setProperty('--ge-po-compare', JSON.stringify(compare));
    btn.setAttribute('aria-label', compare ? `Pre-order ${price}, regular price ${compare}` : `Pre-order ${price}`);
  };
  const buttons = () => document.querySelectorAll('button[data-essential-preorder-element="button"]');
  let queued = false;
  const run = () => { queued = false; buttons().forEach(sync); };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(run); } };
  const observed = new WeakSet();
  const attach = () => buttons().forEach((btn) => {
    if (observed.has(btn)) return;
    observed.add(btn);
    new MutationObserver(schedule).observe(btn, { childList: true, subtree: true, characterData: true });
  });
  attach(); run();
  // The app injects late; poll briefly for new buttons instead of observing the whole body.
  let tries = 0;
  const poll = setInterval(() => { attach(); schedule(); if (++tries >= 20) clearInterval(poll); }, 500);
})();
